import {
  malformed,
  requireByteLength,
  requireEnum,
  requireExactKeys,
  requireInteger,
  requireIsoTimestamp,
  requireRecord,
  requireSha256,
  requireString,
  requireUuid,
} from "./validation";

export const MEDIA_KINDS = ["image", "video"] as const;
export const MEDIA_RENDITIONS = ["thumbnail", "preview", "original"] as const;
export const SHARE_ACCESS_VALUES = ["view", "download"] as const;
export const SUPPORTED_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
] as const;

export const VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH = 2_000_000_000;
export const VIDEO_POSTER_MAX_EDGE_PX = 640;

// Temporary beta switch: use "separate" to restore 480px image thumbnails.
export const IMAGE_THUMBNAIL_MODE: "preview-only" | "separate" = "preview-only";

export type MediaKind = (typeof MEDIA_KINDS)[number];
export type MediaRendition = (typeof MEDIA_RENDITIONS)[number];
export type ShareAccess = (typeof SHARE_ACCESS_VALUES)[number];
export type SupportedVideoMimeType =
  (typeof SUPPORTED_VIDEO_MIME_TYPES)[number];

export interface RenditionDescriptor {
  readonly byteLength: number;
  readonly mimeType: string;
  readonly sha256?: string;
}

export interface MediaRenditions {
  readonly thumbnail: RenditionDescriptor;
  readonly preview?: RenditionDescriptor;
  readonly original?: RenditionDescriptor;
}

export interface MediaUploadAttribution {
  readonly displayName: string;
  readonly recipientId: string;
}

export interface ImageMediaDescriptor {
  readonly mediaId: string;
  readonly kind: "image";
  readonly width: number;
  readonly height: number;
  readonly createdAt?: string;
  readonly uploadedBy?: MediaUploadAttribution;
  readonly renditions: MediaRenditions;
}

export interface VideoMediaDescriptor {
  readonly mediaId: string;
  readonly kind: "video";
  readonly width: number;
  readonly height: number;
  readonly createdAt?: string;
  readonly durationMs: number;
  readonly originalByteLength: number;
  readonly uploadedBy?: MediaUploadAttribution;
  readonly renditions: {
    readonly thumbnail: RenditionDescriptor;
    readonly original: RenditionDescriptor;
  };
}

export type MediaDescriptor = ImageMediaDescriptor | VideoMediaDescriptor;

export interface StorageRendition {
  readonly descriptor: RenditionDescriptor;
  readonly rendition: MediaRendition;
}

export function storageRenditionsForMedia(
  descriptor: MediaDescriptor,
): readonly StorageRendition[] {
  const renditions: StorageRendition[] = [];
  if (
    descriptor.kind === "video"
    || !descriptor.renditions.preview
    || IMAGE_THUMBNAIL_MODE === "separate"
  ) {
    renditions.push({
      descriptor: descriptor.renditions.thumbnail,
      rendition: "thumbnail",
    });
  }
  if (descriptor.kind === "image" && descriptor.renditions.preview) {
    renditions.push({
      descriptor: descriptor.renditions.preview,
      rendition: "preview",
    });
  }
  if (descriptor.renditions.original) {
    renditions.push({
      descriptor: descriptor.renditions.original,
      rendition: "original",
    });
  }
  return renditions;
}

export function parseRenditionDescriptor(
  value: unknown,
  path = "$",
): RenditionDescriptor {
  const record = requireRecord(value, path);
  requireExactKeys(record, ["byteLength", "mimeType"], ["sha256"], path);
  const result: {
    byteLength: number;
    mimeType: string;
    sha256?: string;
  } = {
    byteLength: requireByteLength(record.byteLength, `${path}.byteLength`),
    mimeType: requireString(record.mimeType, `${path}.mimeType`, {
      maxLength: 127,
    }),
  };
  if (record.sha256 !== undefined) {
    result.sha256 = requireSha256(record.sha256, `${path}.sha256`);
  }
  return result;
}

export function parseMediaDescriptor(
  value: unknown,
  path = "$",
): MediaDescriptor {
  const record = requireRecord(value, path);
  const kind = requireEnum(record.kind, MEDIA_KINDS, `${path}.kind`);
  if (kind === "video") {
    return parseVideoDescriptor(record, path);
  }
  return parseImageDescriptor(record, path);
}

function parseImageDescriptor(
  record: Record<string, unknown>,
  path: string,
): ImageMediaDescriptor {
  requireExactKeys(
    record,
    ["mediaId", "kind", "width", "height", "renditions"],
    ["createdAt", "uploadedBy"],
    path,
  );
  const renditionsRecord = requireRecord(
    record.renditions,
    `${path}.renditions`,
  );
  requireExactKeys(
    renditionsRecord,
    ["thumbnail"],
    ["preview", "original"],
    `${path}.renditions`,
  );
  const renditions: {
    thumbnail: RenditionDescriptor;
    preview?: RenditionDescriptor;
    original?: RenditionDescriptor;
  } = {
    thumbnail: parseRenditionDescriptor(
      renditionsRecord.thumbnail,
      `${path}.renditions.thumbnail`,
    ),
  };
  if (renditionsRecord.preview !== undefined) {
    renditions.preview = parseRenditionDescriptor(
      renditionsRecord.preview,
      `${path}.renditions.preview`,
    );
  }
  if (renditionsRecord.original !== undefined) {
    renditions.original = parseRenditionDescriptor(
      renditionsRecord.original,
      `${path}.renditions.original`,
    );
  }
  const result: {
    mediaId: string;
    kind: "image";
    width: number;
    height: number;
    createdAt?: string;
    uploadedBy?: MediaUploadAttribution;
    renditions: MediaRenditions;
  } = {
    mediaId: requireUuid(record.mediaId, `${path}.mediaId`),
    kind: "image",
    width: requireDimension(record.width, `${path}.width`),
    height: requireDimension(record.height, `${path}.height`),
    renditions,
  };
  if (record.createdAt !== undefined) {
    result.createdAt = requireIsoTimestamp(
      record.createdAt,
      `${path}.createdAt`,
    );
  }
  if (record.uploadedBy !== undefined) {
    result.uploadedBy = parseMediaUploadAttribution(
      record.uploadedBy,
      `${path}.uploadedBy`,
    );
  }
  return result;
}

function parseVideoDescriptor(
  record: Record<string, unknown>,
  path: string,
): VideoMediaDescriptor {
  requireExactKeys(
    record,
    [
      "mediaId",
      "kind",
      "width",
      "height",
      "durationMs",
      "originalByteLength",
      "renditions",
    ],
    ["createdAt", "uploadedBy"],
    path,
  );
  const renditionsRecord = requireRecord(
    record.renditions,
    `${path}.renditions`,
  );
  requireExactKeys(
    renditionsRecord,
    ["thumbnail", "original"],
    [],
    `${path}.renditions`,
  );
  const thumbnail = parseRenditionDescriptor(
    renditionsRecord.thumbnail,
    `${path}.renditions.thumbnail`,
  );
  const original = parseRenditionDescriptor(
    renditionsRecord.original,
    `${path}.renditions.original`,
  );
  if (thumbnail.mimeType !== "image/jpeg") {
    throw malformed(
      `${path}.renditions.thumbnail.mimeType`,
      "must be image/jpeg for a video poster",
    );
  }
  requireEnum(
    original.mimeType,
    SUPPORTED_VIDEO_MIME_TYPES,
    `${path}.renditions.original.mimeType`,
  );
  const originalByteLength = requireInteger(
    record.originalByteLength,
    `${path}.originalByteLength`,
    { min: 1, max: VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH },
  );
  if (original.byteLength !== originalByteLength) {
    throw malformed(
      `${path}.originalByteLength`,
      "must equal renditions.original.byteLength",
    );
  }
  const result: {
    mediaId: string;
    kind: "video";
    width: number;
    height: number;
    createdAt?: string;
    durationMs: number;
    originalByteLength: number;
    uploadedBy?: MediaUploadAttribution;
    renditions: {
      thumbnail: RenditionDescriptor;
      original: RenditionDescriptor;
    };
  } = {
    mediaId: requireUuid(record.mediaId, `${path}.mediaId`),
    kind: "video",
    width: requireDimension(record.width, `${path}.width`),
    height: requireDimension(record.height, `${path}.height`),
    durationMs: requireInteger(record.durationMs, `${path}.durationMs`, {
      min: 1,
    }),
    originalByteLength,
    renditions: { thumbnail, original },
  };
  if (record.createdAt !== undefined) {
    result.createdAt = requireIsoTimestamp(
      record.createdAt,
      `${path}.createdAt`,
    );
  }
  if (record.uploadedBy !== undefined) {
    result.uploadedBy = parseMediaUploadAttribution(
      record.uploadedBy,
      `${path}.uploadedBy`,
    );
  }
  return result;
}

function parseMediaUploadAttribution(
  value: unknown,
  path: string,
): MediaUploadAttribution {
  const record = requireRecord(value, path);
  requireExactKeys(record, ["displayName", "recipientId"], [], path);
  return {
    displayName: requireString(record.displayName, `${path}.displayName`, {
      maxLength: 120,
    }),
    recipientId: requireUuid(record.recipientId, `${path}.recipientId`),
  };
}

function requireDimension(value: unknown, path: string): number {
  return requireInteger(value, path, { min: 1, max: 65_535 });
}
